/**
 * The pool settings Chat card, §27.2 card 8, mounted as the `chat` slot's
 * children (ISSUE-1040). It renders no card shell: `PoolSettingsCardSlot` owns
 * the shell and the title.
 *
 * The `Chat` switch writes `/pool/update { chatEnabled }`. A membership
 * change resends `pools.chatEnabled` to messaging-api, so the chat row is
 * not a second switch. `Who can post` writes
 * `/chat/set-post-policy` for this pool's chat.
 *
 * No confirm before turning chat off, a deliberate deviation from design 27
 * card 8: `audience-persona` keeps blocking confirms for genuinely destructive
 * actions, and turning chat off deletes nothing (MSG-10). ISSUE-1040 "Owner
 * default applied" 1.
 *
 * Each control keeps the value the server last accepted on this mount until
 * the server read catches up to it (`settleAcceptedValue`), so another Admin's
 * later change shows here too, once this card's own post-write refetch has
 * landed. See `chatSettingsCardState.ts` for the one narrow window that rule
 * leaves open.
 */
import {
  useChatGet,
  useChatListMine,
  useChatSetPostPolicy,
} from "@sportsmet/messaging-sdk/private";
import { usePoolUpdate } from "@sportsmet/pool-sdk/private";
import { Radio, SavedNote, Switch } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { findPoolChat } from "../poolHome/poolHomeState";
import {
  CHAT_SETTINGS_POST_POLICY_HELP,
  CHAT_SETTINGS_POST_POLICY_ITEMS,
  CHAT_SETTINGS_POST_POLICY_LABEL,
  CHAT_SETTINGS_SWITCH_DESCRIPTION,
  CHAT_SETTINGS_SWITCH_LABEL,
  readPostPolicyValue,
  resolveChatSettingsCard,
  settleAcceptedValue,
  type typeChatPostPolicy,
} from "./chatSettingsCardState";

export type typePoolSettingsChatCardProps = {
  poolId: string;
  poolChatEnabled: boolean | undefined;
};

export const PoolSettingsChatCard = ({
  poolId,
  poolChatEnabled,
}: typePoolSettingsChatCardProps): ReactNode => {
  const update = usePoolUpdate();
  const setPostPolicy = useChatSetPostPolicy();
  const [chatEnabledInFlight, setChatEnabledInFlight] = useState<
    boolean | null
  >(null);
  const [chatEnabledAccepted, setChatEnabledAccepted] = useState<
    boolean | null
  >(null);
  const [postPolicyInFlight, setPostPolicyInFlight] =
    useState<typeChatPostPolicy | null>(null);
  const [postPolicyAccepted, setPostPolicyAccepted] =
    useState<typeChatPostPolicy | null>(null);
  const [savedField, setSavedField] = useState<
    "chatEnabled" | "postPolicy" | null
  >(null);

  const chats = useChatListMine({});
  const items = chats.data?.data?.items ?? null;
  const chatId = findPoolChat({ poolId, items })?.chatId ?? null;
  const chatGet = useChatGet(
    { chatId: chatId ?? "" },
    { enabled: chatId !== null },
  );
  const serverPostPolicy = chatGet.data?.data?.postPolicy ?? null;

  useEffect(() => {
    const settled = settleAcceptedValue({
      accepted: chatEnabledAccepted,
      server: poolChatEnabled,
    });
    if (settled !== chatEnabledAccepted) setChatEnabledAccepted(settled);
  }, [chatEnabledAccepted, poolChatEnabled]);
  useEffect(() => {
    const settled = settleAcceptedValue({
      accepted: postPolicyAccepted,
      server: serverPostPolicy,
    });
    if (settled !== postPolicyAccepted) setPostPolicyAccepted(settled);
  }, [postPolicyAccepted, serverPostPolicy]);

  const model = resolveChatSettingsCard({
    poolChatEnabled,
    chatEnabledInFlight,
    chatEnabledAccepted,
    isChatListLoaded: items !== null,
    isChatListFailed: chats.isError || extractErrorCode(chats.data) !== null,
    chatId,
    serverPostPolicy,
    isPostPolicyFailed:
      chatGet.isError || extractErrorCode(chatGet.data) !== null,
    postPolicyInFlight,
    postPolicyAccepted,
  });

  async function handleChatEnabledChange(next: boolean): Promise<void> {
    setChatEnabledInFlight(next);
    setSavedField(null);
    try {
      const result = await update.mutateAsync({ poolId, chatEnabled: next });
      if (showApiError(result)) {
        setChatEnabledInFlight(null);
        return;
      }
      setChatEnabledAccepted(result.data?.chatEnabled ?? next);
      setChatEnabledInFlight(null);
      setSavedField("chatEnabled");
    } catch {
      showOfflineError();
      setChatEnabledInFlight(null);
    }
  }

  async function handlePostPolicyChange(raw: string): Promise<void> {
    const next = readPostPolicyValue(raw);
    if (next === null || chatId === null) return;
    setPostPolicyInFlight(next);
    setSavedField(null);
    try {
      const result = await setPostPolicy.mutateAsync({
        chatId,
        postPolicy: next,
      });
      if (showApiError(result)) {
        setPostPolicyInFlight(null);
        return;
      }
      setPostPolicyAccepted(result.data?.postPolicy ?? next);
      setPostPolicyInFlight(null);
      setSavedField("postPolicy");
    } catch {
      showOfflineError();
      setPostPolicyInFlight(null);
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="pool-settings-chat-card">
      <div
        className="flex flex-col gap-1"
        data-testid="pool-settings-chat-switch"
      >
        <Switch
          label={CHAT_SETTINGS_SWITCH_LABEL}
          description={CHAT_SETTINGS_SWITCH_DESCRIPTION}
          checked={model.chatSwitch.checked}
          disabled={model.chatSwitch.disabled}
          onCheckedChange={(next) => {
            void handleChatEnabledChange(next);
          }}
        />
        <div data-testid="pool-settings-chat-saved-chatEnabled">
          <SavedNote saved={savedField === "chatEnabled"} />
        </div>
      </div>
      <div
        className="flex flex-col gap-1"
        data-testid="pool-settings-chat-post-policy"
      >
        <Radio
          label={CHAT_SETTINGS_POST_POLICY_LABEL}
          items={CHAT_SETTINGS_POST_POLICY_ITEMS}
          value={model.postPolicy.value ?? ""}
          disabled={model.postPolicy.disabled}
          orientation="horizontal"
          onValueChange={(raw) => {
            void handlePostPolicyChange(raw);
          }}
        />
        <p className="text-caption text-ink-secondary">
          {CHAT_SETTINGS_POST_POLICY_HELP}
        </p>
        {model.postPolicy.disabledReason === null ? null : (
          <p
            className="text-caption text-ink-secondary"
            data-testid="pool-settings-chat-post-policy-reason"
          >
            {model.postPolicy.disabledReason}
          </p>
        )}
        <div data-testid="pool-settings-chat-saved-postPolicy">
          <SavedNote saved={savedField === "postPolicy"} />
        </div>
      </div>
    </div>
  );
};
